import {
	Box,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { ASSETS_BASE_URL, SERVER_BASE_URL } from "../../config";
import { useSite } from "../../context/siteContext";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/cartContext";
import { useAuth } from "../../context/authContext";
import { BUTTON, LABELS, ROUTES, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { saveRequestList } from "../../services/productCardService";
import { toast } from "../../context/ToastContext";

import formatCurrency from "../../utilities/currency";
import { AddShoppingCart } from "@mui/icons-material";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import ReplacementPartNotice from "../ReplacementPartNotice/ReplacementPartNotice";
import { getMinimumSalesOrderQuantity, getMultipleSalesOrderQuantity, normalizeOrderQuantity } from "../../utilities/orderQuantity";
import { getEffectiveProductTarget } from "../../utilities/replacementProduct";
const TopProductListItem = ({
	product,
	machine_id,
	source = "products_page",
}) => {
	const navigate = useNavigate();
	const { selectedSite, selectedSiteCurrency } = useSite();
	const [isLoading, setIsLoading] = useState(false); // To manage the loading state
	const [isSuccess, setIsSuccess] = useState(false);
	const { addToCart } = useCart();
	const { user, canViewProductPrice, canAddToCart, canAddToRequestList } = useAuth();
    const { stockImageUrl } = useBrandingAssets();
    const [imageSrc, setImageSrc] = useState(stockImageUrl || `${ASSETS_BASE_URL}/stock.png`);
	const [price, setPrice] = useState(0);
	const { t } = useTranslation();
	const canShowPrice = canViewProductPrice();
	const canShowAddToCart = canAddToCart();
	const canShowAddToRequestList = canAddToRequestList();
	const triggerToast = (message, severity = "success") => {
		if (!message) return;
		toast.trigger({ message, severity });
	};

	useEffect(() => {
		const raw = product?.asset_data?.images?.[0];
		if (raw) {
			if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) {
				setImageSrc(raw);
				return;
			}
			const path = raw.startsWith("/") ? raw : `/${raw}`;
			setImageSrc(`${ASSETS_BASE_URL}${path}`);
			return;
		}
		if (product?.product_number) {
			setImageSrc(`${ASSETS_BASE_URL}/ProductImages/TMB/${product.product_number}.JPG`);
			return;
		}
		setImageSrc(stockImageUrl || `${ASSETS_BASE_URL}/stock.png`);
		// eslint-disable-next-line
	}, [product.product_number, product?.asset_data, stockImageUrl]);

	useEffect(() => {
		if (product && selectedSiteCurrency) {
			const key = `price_${selectedSiteCurrency}`;
			const currencyPrice = product[key];
			const finalPrice = Number(product.purchasable) === 1 ? currencyPrice : 0;
			
			setPrice(finalPrice);
		}
	}, [product, selectedSiteCurrency]);

	const handleProductPageNaviagte = (product_id) => {
		// The part page route needs BOTH ids. machine_id comes from a LEFT JOIN on the bill of
		// materials in the recommendations query, so it is null for any suggested part that is in no
		// machine's BOM — 1,219 of the parts that appear as co-purchase partners, 0.2%. Those clicks
		// used to navigate to "/machines/undefined/<id>", which is not a route and lands on the
		// error page. Same defect the home-page panels had, and the same resolution: fall back to a
		// search for the part number rather than inventing a machine association.
		const resolvedMachineId = machine_id || product?.machine_id;
		if (!resolvedMachineId || !product_id) {
			const number = product?.product_number;
			if (number) navigate(`${ROUTES.SEARCH_RESULTS}?q=${encodeURIComponent(number)}`);
			return;
		}
		navigate(`/machines/${resolvedMachineId}/${product_id}`);
	};
	const actionTarget = getEffectiveProductTarget(product, {
		machineId: machine_id || product?.machine_id,
		currency: selectedSiteCurrency,
	});
	const quantityProduct = actionTarget?.replaced ? actionTarget : product;
	const isPurchasable = actionTarget?.replaced
		? actionTarget.purchasable
		: Number(product.purchasable) === 1;
	const displayPrice = actionTarget?.replaced ? actionTarget.price : price;
	const displayLineDiscountApplied = actionTarget?.replaced
		? actionTarget.line_discount_applied
		: product.line_discount_applied;
	const handleAddToCart = async (event) => {
		event.stopPropagation();
		if (!canShowAddToCart) return;

		// Start the loading state and reset success state
		setIsLoading(true);
		setIsSuccess(false);
		try {
			const minimumSalesOrderQuantity = getMinimumSalesOrderQuantity(quantityProduct);
			const multipleSalesOrderQuantity = getMultipleSalesOrderQuantity(quantityProduct);
			// Call the addToCart function (which you've defined to add the product)
			await addToCart({
				...product,
				product_id: actionTarget?.product_id || product.product_id,
				ProductID: actionTarget?.product_id || product.product_id,
				machine_id: actionTarget?.machine_id || machine_id || product.machine_id,
				MachineID: actionTarget?.machine_id || machine_id || product.machine_id,
				Quantity: normalizeOrderQuantity(1, quantityProduct),
				Site: selectedSite,
				minimum_sales_order_quantity: minimumSalesOrderQuantity,
				multiple_sales_order_quantity: multipleSalesOrderQuantity,
			});

			// Set success state
			setIsSuccess(true);
			triggerToast(
				t("ALERT_MESSAGES.PRODUCT_ADDED_TO_CART", { productNumber: product.product_number }),
				"success"
			);
			// Reset the button text back to "Add to Cart" after 2 seconds
			setTimeout(() => {
				setIsLoading(false);
				setIsSuccess(false);
			}, 2000); // 2 seconds delay
		} catch (error) {
			// Handle any error (optional)
			setIsSuccess(false);
			triggerToast(`Error adding ${product.product_name} to cart`, "error");
		} finally {
			// Stop the loading spinner after a delay and reset the button state
			setIsLoading(false);
		}
	};

	const handleAddToRequestList = async (event) => {
		event.stopPropagation();
		if (!canShowAddToRequestList) return;

		// Start the loading state and reset success state
		setIsLoading(true);
		setIsSuccess(false);
		try {
			// Call the addToCart function (which you've defined to add the product)
			// Set success state
			const req_body = {
				machine_id: actionTarget?.machine_id || machine_id,
				product_id: actionTarget?.product_id || product.product_id,
				site_number: selectedSite,
				user_id: user.user_id,
			};

			try {
				const response = await saveRequestList(req_body);
				if (response.status) {
					setIsSuccess(true);
					setIsLoading(false);
					triggerToast(
						t("ALERT_MESSAGES.PRODUCT_ADDED_TO_REQUEST", {
							productName: product.product_name,
						}),
						"success"
					);

					setTimeout(() => {
						setIsLoading(false);
						setIsSuccess(false);
					}, 1000);
				} else if (!response.status) {
					triggerToast(
						t("ALERT_MESSAGES.PRODUCT_ALREADY_IN_REQUEST", {
							productName: product.product_name,
						}),
						"warning"
					);
				}
			} catch (error) {
				console.error("Error fetching product name:", error);
			}

			// Reset the button text back to "Add to Cart" after 2 seconds
			// 2 seconds delay
		} catch (error) {
			// Handle any error (optional)
			setIsSuccess(false);
			triggerToast(`Error adding ${product.product_name} to request list`, "error");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<Box
				sx={{
					width: "100%",
					// Raised from 75px (UAT 2026-08-14): the recommendation-reason line below now clamps
					// to 2 lines instead of 1, and 75px only had room for one.
					minHeight: "75px",
					borderRadius: 2,
					display: "flex",
					flexDirection: "row",
					justifyContent: "flex-start",
					alignItems: "center",
					backgroundColor: "#fff",
					boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)",
					p: 2,
					columnGap: 2,	
					margin: "7px 0",
					"&:hover": {
						boxShadow: "0 0 10px 2px rgba(0,0,0,0.2)",
					},
				}}
				onClick={() => {
					handleProductPageNaviagte(product.product_id);
				}}
			>
                <img
                    style={{ height: "50px", width: "50px", flexShrink: 0 }}
                    src={imageSrc}
                    alt={product.product_name}
                    onError={(e) => { e.currentTarget.src = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`; }}
                />
				{/* minWidth:0 + width:100% on the lines lets the name and part/machine line
				    run all the way to the action icons before they ellipse, instead of
				    stopping at a fixed maxWidth well short of them.

				    minWidth:0 is also load-bearing for overflow, not only for width. A flex child
				    defaults to min-width:auto, which is its CONTENT width — so a nowrap reason line
				    like "Part of the same assembly on your AMF/FANUC Robotic Pan Stacking,
				    Unstacking Cells (Pan Storage System By Others)" (79 characters) forced the card,
				    its grid column and the whole page wider than the viewport. None of the ellipsis
				    styling below can take effect until the box is allowed to shrink. */}
				<Box sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
					<Tooltip title={product.product_name}>
						<Typography
							variant="body1"
							sx={{
								fontWeight: "bold",
								fontSize: "12px",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								width: "100%",
							}}
						>
							{product.product_name}
						</Typography>
					</Tooltip>

					{/* width:100% rather than a fixed maxWidth. The old 175px cap was sized for a
					    full-width list row; in a 3-up grid cell the card is wider than that, so the
					    cap both wasted space and truncated part numbers that would otherwise fit. */}
					<Tooltip
						title={
							source === "search_results"
								? `${t("UI_TEXT.PART")} ${product.product_number || ""} | ${t("UI_TEXT.MACHINE")} ${product.machine_number || ""}`
								: `${t("UI_TEXT.PART")} ${product.product_number || ""}`
						}
					>
						<Typography
							variant="body1"
							color="text.secondary"
							sx={{
								fontSize: "11px",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								width: "100%",
							}}
						>
							{source === "search_results" ? (
								<>
									<b>{t("UI_TEXT.PART")} </b> {product.product_number} {" | "}
									<b>{t("UI_TEXT.MACHINE")} </b> {product.machine_number}
								</>
							) : (
								<>
										<b>{t("UI_TEXT.PART")} </b> {product.product_number}
								</>
							)}
						</Typography>
					</Tooltip>
					<ReplacementPartNotice product={product} />

					{/* WHY this part was suggested. AMF's review made an explainable recommendation the
					    condition for trusting the feature at all: "my experience so far with AI has not
					    been great that it's correct". A bare part number cannot be checked; a reason can.
					    Only rendered when the API supplies one, so nothing is invented in the UI.

					    UAT 2026-08-14: this used to be a single line, ellipsis-truncated, full text only
					    on hover. Changed to a 2-line clamp so the reason is readable without hovering —
					    the card's maxHeight below was raised to make room. */}
					{product.recommendation_reason && (
						<Typography
							variant="body2"
							title={product.recommendation_reason}
							sx={{
								fontSize: "10px",
								lineHeight: 1.3,
								mt: 0.35,
								// Grey, not the brand red. Red is this UI's warning colour — on the
								// coverage panel it marks parts never ordered — so a red reason line read
								// as a problem with the part rather than as the reason it is being
								// suggested. AMF: "suggestion should be gray, red looks warning".
								color: "text.secondary",
								// Two lines, not one. A single truncated line hid the end of longer
								// reasons ("Part of the same assembly on your...") behind a hover, which
								// is not discoverable. The card's minHeight was raised to suit.
								display: "-webkit-box",
								WebkitLineClamp: 2,
								WebkitBoxOrient: "vertical",
								whiteSpace: "normal",
								overflow: "hidden",
								textOverflow: "ellipsis",
								maxWidth: "100%",
							}}
						>
							{product.recommendation_reason}
						</Typography>
					)}
					{source === "products_page" && canShowPrice && (
						<Typography
							variant="body1"
							color="primary"
							sx={{ fontSize: "14px" }}
						>
							<b>
								{!isPurchasable
									? ""
									: (displayPrice === "0.00" || displayPrice === 0) && !displayLineDiscountApplied
										? ""
										: formatCurrency(
											displayPrice,
											selectedSiteCurrency
									)}
							</b>
						</Typography>
					)}
				</Box>

				<Box
					display={"flex"}
					justifyContent={"flex-end"}
					alignItems={"center"}
					columnGap={2}
					sx={{ flexShrink: 0 }}
				>
					{source !== "products_page" && canShowPrice && (
						<Typography
							variant="body1"
							color="primary"
							sx={{ fontSize: "14px" }}
						>
							<b>
								{!isPurchasable
									? ""
									: (displayPrice === "0.00" || displayPrice === 0) && !displayLineDiscountApplied
										? ""
										: formatCurrency(
											displayPrice,
											selectedSiteCurrency
									)}
							</b>
						</Typography>
					)}
					{!isPurchasable && canShowAddToRequestList ? (
						<Tooltip title={t("BUTTON.ADD_TO_REQUEST_LIST")}>
							<IconButton
								color="primary"
								onClick={handleAddToRequestList}
							>
								{/* Different icon or style here */}
								<SummarizeIcon />{" "}
								{/* example: a blocked icon */}
							</IconButton>
						</Tooltip>
					) : product.purchasable !== 0 && canShowAddToCart ? (
							<Tooltip title={t("LABELS.ADD_TO_CART")}>
								<IconButton
									color="primary"
									onClick={handleAddToCart}
								>
									<AddShoppingCart />
							</IconButton>
						</Tooltip>
					) : null}
				</Box>
			</Box>
		</>
	);
};

export default TopProductListItem;
